import React, { useState, useEffect } from 'react';
import '@/App.css';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthPage } from './pages/AuthPage';
import LandingPage from './pages/LandingPage';
import { Dashboard } from './pages/Dashboard';
import { WorkoutsPage } from './pages/WorkoutsPage';
import { WorkoutPlanPage } from './pages/WorkoutPlanPage';
import { FeedPage } from './pages/FeedPage';
import { ShopPage } from './pages/ShopPage';
import AdminPanel from './pages/AdminPanel';
import { AdminWorkoutManager } from './pages/AdminWorkoutManager';
import { UserWorkoutsList } from './pages/UserWorkoutsList';
import { UserWorkoutManager } from './pages/UserWorkoutManager';
import { RankingPage } from './pages/RankingPage';
import { AchievementsPage } from './pages/AchievementsPage';
import { MyFeedbacksPage } from './pages/MyFeedbacksPage';
import { EditProfilePage } from './pages/EditProfilePage';
import CalendarPage from './pages/CalendarPage';
import StudentCalendarPage from './pages/StudentCalendarPage';
import { BottomNav } from './components/BottomNav';
import { TopHeader } from './components/TopHeader';
import { Toaster } from './components/ui/sonner';
import { getStoredToken, getStoredUser, setStoredUser, setOnAuthError, validateSession, clearAuth } from './utils/api';
import { motion } from 'framer-motion';
import { LogOut } from 'lucide-react';
import PWAInstallPrompt from './components/PWAInstallPrompt';
import { NetworkStatus } from './components/NetworkStatus';

function App() {
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);

  // Force logout callback (called by axios interceptor on 401)
  const forceLogout = React.useCallback(() => {
    setUser(null);
    setIsAuthenticated(false);
    clearAuth();
  }, []);

  // Register the auth error callback so axios interceptor can trigger logout
  useEffect(() => {
    setOnAuthError(forceLogout);
    return () => setOnAuthError(null);
  }, [forceLogout]);

  // On mount: validate session with backend (not just localStorage)
  useEffect(() => {
    const initSession = async () => {
      const token = getStoredToken();
      const storedUser = getStoredUser();
      if (!token || !storedUser) {
        setLoading(false);
        return;
      }
      // Show stored user immediately for fast UI
      setUser(storedUser);
      setIsAuthenticated(true);
      // Then validate with backend in background
      const freshUser = await validateSession();
      if (freshUser) {
        setUser(freshUser);
      } else {
        // Token invalid — force re-login
        setUser(null);
        setIsAuthenticated(false);
      }
      setLoading(false);
    };
    initSession();
  }, []);

  // Re-validate session when app comes back from background
  useEffect(() => {
    const handleVisibilityChange = async () => {
      if (document.visibilityState === 'visible' && isAuthenticated) {
        const freshUser = await validateSession();
        if (freshUser) {
          setUser(freshUser);
        }
        // If null, the 401 interceptor already handles logout
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [isAuthenticated]);

  const handleAuthSuccess = (userData) => {
    setUser(userData);
    setStoredUser(userData);
    setIsAuthenticated(true);
  };

  const handleLogout = () => {
    setUser(null);
    setIsAuthenticated(false);
    clearAuth();
    window.location.href = '/';
  };

  const handleUserUpdate = (updatedUser) => {
    setUser(updatedUser);
    setStoredUser(updatedUser);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-neon-lime border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-zinc-400 font-secondary">Carregando...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="App">
        <BrowserRouter>
          <Routes>
            <Route path="/" element={<AuthPage onAuthSuccess={handleAuthSuccess} />} />
            <Route path="/login" element={<AuthPage onAuthSuccess={handleAuthSuccess} />} />
            <Route path="/treinar" element={<LandingPage />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
        <Toaster position="top-right" />
      </div>
    );
  }

  if (!user.is_active && user.role !== 'admin') {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center p-6">
        <motion.div
          className="max-w-md w-full glass-surface rounded-2xl p-8 text-center"
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
        >
          <div className="w-20 h-20 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
            <span className="text-4xl">⚠️</span>
          </div>
          <h2 className="font-primary font-black text-3xl text-white uppercase mb-4">
            Conta Suspensa
          </h2>
          <p className="text-zinc-400 font-secondary mb-6">
            Sua participação está temporariamente suspensa. Entre em contato
            com seu trainer para mais informações.
          </p>
          <button
            onClick={handleLogout}
            className="bg-zinc-800 hover:bg-zinc-700 text-white font-bold py-3 px-6 rounded-lg transition-colors flex items-center gap-2 mx-auto"
          >
            <LogOut className="w-5 h-5" />
            Sair
          </button>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="App">
      <BrowserRouter>
        <NetworkStatus />
        <div className="min-h-screen bg-[#050505] pt-20 pb-24">
          <TopHeader user={user} onLogout={handleLogout} />
          <div className="max-w-screen-2xl mx-auto">
            <Routes>
              <Route path="/" element={<Navigate to="/dashboard" replace />} />
              <Route
                path="/dashboard"
                element={
                  <Dashboard user={user} onUserUpdate={handleUserUpdate} />
                }
              />
              <Route
                path="/workouts"
                element={
                  <WorkoutPlanPage user={user} onUserUpdate={handleUserUpdate} />
                }
              />
              <Route path="/feed" element={<FeedPage user={user} />} />
              <Route path="/community" element={<FeedPage user={user} />} />
              <Route
                path="/shop"
                element={<ShopPage user={user} onUserUpdate={handleUserUpdate} />}
              />
              <Route
                path="/ranking"
                element={<RankingPage user={user} />}
              />
              <Route
                path="/achievements"
                element={<AchievementsPage />}
              />
              <Route
                path="/my-feedbacks"
                element={<MyFeedbacksPage user={user} />}
              />
              <Route
                path="/edit-profile"
                element={<EditProfilePage user={user} onUserUpdate={handleUserUpdate} />}
              />
              <Route
                path="/calendar"
                element={<CalendarPage />}
              />
              {user.role === 'admin' && (
                <>
                  <Route path="/admin" element={<AdminPanel user={user} />} />
                  <Route path="/admin/workouts" element={<AdminWorkoutManager user={user} />} />
                  <Route path="/admin/users-workouts" element={<UserWorkoutsList user={user} />} />
                  <Route path="/admin/users/:userId/workouts" element={<UserWorkoutManager user={user} />} />
                  <Route path="/admin/student/:userId/calendar" element={<StudentCalendarPage />} />
                </>
              )}
              <Route path="/treinar" element={<Navigate to="/dashboard" replace />} />
            </Routes>
          </div>
          <BottomNav user={user} />
          <PWAInstallPrompt />
        </div>
      </BrowserRouter>
      <Toaster position="top-right" richColors />
    </div>
  );
}

export default App;
